import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiUser, FiLock, FiEye, FiEyeOff, FiArrowRight } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';

const LoginContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-sizing: border-box;
  padding: clamp(1rem, 3vw, 2rem);
`;

const LoginCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  width: 100%;
  max-width: clamp(320px, 90vw, 400px);
  position: relative;
  z-index: 1;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
  padding: clamp(1.5rem, 4vw, 2.5rem);
  border-radius: clamp(8px, 2vw, 12px);

  @media (max-width: 480px) {
    max-width: 100%;
    margin: 0 1rem;
    padding: clamp(1.25rem, 3vw, 1.75rem);
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: clamp(1.5rem, 4vw, 2rem);
  
  .logo {
    background: linear-gradient(135deg, #667eea, #764ba2);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
    box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3);
    width: clamp(40px, 8vw, 48px);
    height: clamp(40px, 8vw, 48px);
    font-size: clamp(1.25rem, 3vw, 1.5rem);
    margin-bottom: clamp(0.75rem, 2vw, 1rem);
    border-radius: clamp(6px, 1.5vw, 8px);
  }
  
  h1 {
    font-weight: 700;
    color: #1a202c;
    font-size: clamp(1.125rem, 3vw, 1.25rem);
    margin-bottom: clamp(0.375rem, 1vw, 0.5rem);
  }
  
  p {
    color: #718096;
    font-size: clamp(0.75rem, 2vw, 0.875rem);
    margin: 0;
  }

  @media (max-width: 480px) {
    margin-bottom: clamp(1.25rem, 3vw, 1.5rem);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: clamp(1.25rem, 3vw, 1.5rem);

  @media (max-width: 480px) {
    gap: clamp(1rem, 2.5vw, 1.25rem);
  }
`;

const FormGroup = styled.div`
  position: relative;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: #2d3748;
  font-size: clamp(0.75rem, 2vw, 0.875rem);
  margin-bottom: clamp(0.375rem, 1vw, 0.5rem);

  @media (max-width: 480px) {
    font-size: clamp(0.6875rem, 1.75vw, 0.75rem);
  }
`;

const InputWrapper = styled.div`
  position: relative;
  
  .input-icon {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    color: #a0aec0;
    left: clamp(10px, 2vw, 12px);
    font-size: clamp(0.875rem, 2vw, 1rem);
  }
  
  .password-toggle {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    color: #a0aec0;
    cursor: pointer;
    right: clamp(10px, 2vw, 12px);
    font-size: clamp(0.875rem, 2vw, 1rem);
    
    &:hover {
      color: #667eea;
    }
  }

  @media (max-width: 480px) {
    .input-icon {
      left: clamp(8px, 1.5vw, 10px);
      font-size: clamp(0.8125rem, 1.75vw, 0.875rem);
    }
    
    .password-toggle {
      right: clamp(8px, 1.5vw, 10px);
      font-size: clamp(0.8125rem, 1.75vw, 0.875rem);
    }
  }
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  background: #ffffff;
  color: #2d3748;
  box-sizing: border-box;
  padding: clamp(0.625rem, 2vw, 0.75rem) clamp(0.625rem, 2vw, 0.75rem) clamp(0.625rem, 2vw, 0.75rem) clamp(2.25rem, 4vw, 2.5rem);
  font-size: clamp(0.75rem, 2vw, 0.875rem);
  border-radius: clamp(6px, 1.5vw, 8px);
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: #cbd5e0;
  }

  @media (max-width: 480px) {
    padding: clamp(0.5rem, 1.5vw, 0.625rem) clamp(0.5rem, 1.5vw, 0.625rem) clamp(0.5rem, 1.5vw, 0.625rem) clamp(2rem, 3.5vw, 2.25rem);
    font-size: clamp(0.6875rem, 1.75vw, 0.75rem);
  }
`;

const PasswordInput = styled(Input)`
  padding-right: clamp(2.25rem, 4vw, 2.5rem);

  @media (max-width: 480px) {
    padding-right: clamp(2rem, 3.5vw, 2.25rem);
  }
`;

const LoginButton = styled.button`
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(0.625rem, 2vw, 0.75rem) clamp(1rem, 3vw, 1.25rem);
  font-size: clamp(0.75rem, 2vw, 0.875rem);
  gap: clamp(0.375rem, 1vw, 0.5rem);
  border-radius: clamp(6px, 1.5vw, 8px);
  margin-top: clamp(0.375rem, 1vw, 0.5rem);
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
  
  svg {
    font-size: clamp(0.75rem, 2vw, 0.875rem);
  }

  @media (max-width: 480px) {
    padding: clamp(0.5rem, 1.5vw, 0.625rem) clamp(0.875rem, 2.5vw, 1rem);
    font-size: clamp(0.6875rem, 1.75vw, 0.75rem);
    gap: clamp(0.25rem, 0.75vw, 0.375rem);
    
    svg {
      font-size: clamp(0.6875rem, 1.75vw, 0.75rem);
    }
  }
`;

const ErrorMessage = styled.div`
  background: #fed7d7;
  color: #c53030;
  text-align: center;
  font-weight: 500;
  padding: clamp(0.625rem, 2vw, 0.75rem);
  font-size: clamp(0.75rem, 2vw, 0.875rem);
  border-radius: clamp(6px, 1.5vw, 8px);
  border: 1px solid #feb2b2;

  @media (max-width: 480px) {
    padding: clamp(0.5rem, 1.5vw, 0.625rem);
    font-size: clamp(0.6875rem, 1.75vw, 0.75rem);
  }
`;

const Login = () => {
  const [employeeId, setEmployeeId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(employeeId, password);
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        <Header>
          <div className="logo">✈️</div>
          <h1>Welcome Back</h1>
          <p>Sign in to your account</p>
        </Header>

        <Form onSubmit={handleSubmit}>
          {error && <ErrorMessage>{error}</ErrorMessage>}

          <FormGroup>
            <Label>Employee ID</Label>
            <InputWrapper>
              <Input
                type="text"
                placeholder="Enter employee ID"
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                required
              />
              <FiUser className="input-icon" />
            </InputWrapper>
          </FormGroup>

          <FormGroup>
            <Label>Password</Label>
            <InputWrapper>
              <PasswordInput
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <FiLock className="input-icon" />
              <div
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </div>
            </InputWrapper>
          </FormGroup>

          <LoginButton type="submit" disabled={loading}>
            {loading ? 'Signing In...' : 'Sign In'}
            <FiArrowRight />
          </LoginButton>
        </Form>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login;